import React, { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import { cardBackground, cardBorder } from '../styles/themeHelpers';
import { initiatePasswordReset } from '../services/passwordResetService';
import AnimatedBackground from '../components/common/AnimatedBackground';

const Background = styled.div`
  min-height: 100vh;
  background: ${({ theme }) =>
    theme.profile?.pageBackground === 'transparent'
      ? 'transparent'
      : theme.gradients?.background || theme.colors.background};
  position: relative;
  overflow: hidden;
`;

const CenterBlock = styled.div`
  position: relative;
  z-index: 2;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 100vh;
  padding: 20px;
`;

const AccessFrame = styled.div`
  background: ${({ theme }) => cardBackground(theme)};
  border: 2px solid ${({ theme }) => cardBorder(theme)};
  box-shadow: 0 0 30px rgba(0, 212, 255, 0.3);
  padding: 30px;
  border-radius: 12px;
  max-width: 420px;
  width: 100%;
  position: relative;
  text-align: center;
  z-index: 10;
  backdrop-filter: none;

  @media (max-width: 768px) {
    padding: 25px 20px;
  }
`;

const AccessTitle = styled.h1`
  font-size: 1.5rem;
  color: #00d4ff;
  text-transform: uppercase;
  letter-spacing: 2px;
  margin-bottom: 10px;
  font-weight: bold;
  text-shadow: 0 0 10px rgba(0, 212, 255, 0.5);
`;

const SubStatus = styled.div`
  color: #b0b0b0;
  font-size: 13px;
  margin-bottom: 25px;
  letter-spacing: 1px;
`;

const InputBlock = styled.div`
  margin-bottom: 15px;
`;

const InputLabel = styled.div`
  color: #00d4ff;
  font-size: 13px;
  margin-bottom: 8px;
  letter-spacing: 1px;
  text-align: left;
  font-weight: 500;
`;

const InputField = styled.input`
  width: 100%;
  background: rgba(255, 255, 255, 0.05);
  border: 1px solid rgba(0, 212, 255, 0.3);
  border-radius: 6px;
  color: #fff;
  font-size: 14px;
  padding: 12px 14px;
  outline: none;
  text-align: left;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:focus {
    border-color: #00d4ff;
    box-shadow: 0 0 10px rgba(0, 212, 255, 0.2);
  }

  &::placeholder {
    color: #666;
  }
`;

const StatusText = styled.div`
  color: #b0b0b0;
  font-size: 11px;
  margin-top: 8px;
  letter-spacing: 1px;
  text-align: left;
`;

const SubmitButton = styled.button`
  width: 100%;
  background: linear-gradient(135deg, #00d4ff, #0099cc);
  border: none;
  border-radius: 8px;
  color: white;
  font-size: 14px;
  font-weight: bold;
  text-transform: uppercase;
  padding: 12px 0;
  margin: 20px 0;
  cursor: pointer;
  letter-spacing: 1px;
  transition: all 0.3s ease;

  &:hover:not(:disabled) {
    background: linear-gradient(135deg, #00a8cc, #007799);
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 212, 255, 0.3);
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
    transform: none;
  }
`;

const LinkRow = styled.div`
  display: flex;
  justify-content: space-between;
  gap: 15px;
  margin-top: 15px;
`;

const FrameLink = styled(Link)`
  color: #00d4ff;
  font-size: 12px;
  text-decoration: none;
  letter-spacing: 1px;
  padding: 6px 10px;
  border: 1px solid transparent;
  border-radius: 4px;
  transition: all 0.3s ease;

  &:hover {
    color: #fff;
    border-color: rgba(0, 212, 255, 0.5);
    background: rgba(0, 212, 255, 0.1);
  }
`;

const ErrorMsg = styled.div`
  color: #ff4444;
  font-size: 13px;
  text-align: center;
  margin-bottom: 15px;
  padding: 10px;
  background: rgba(255, 68, 68, 0.1);
  border: 1px solid rgba(255, 68, 68, 0.3);
  border-radius: 6px;
`;

const SuccessMsg = styled.div`
  color: #4caf50;
  font-size: 14px;
  text-align: center;
  margin-bottom: 20px;
  padding: 15px;
  border: 1px solid rgba(76, 175, 80, 0.3);
  background: rgba(76, 175, 80, 0.1);
  border-radius: 8px;
`;

const ForgotPasswordPage: React.FC = () => {
  const [email, setEmail] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const navigate = useNavigate();

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const response = await initiatePasswordReset(email);

      if (response.success) {
        sessionStorage.setItem('resetEmail', email);
        sessionStorage.setItem('resetMessage', response.message);
        navigate('/reset-password-code');
      } else {
        setError(response.message);
      }
    } catch (err) {
      setError('Error processing request. Please try again later.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Background>
      <AnimatedBackground />

      <CenterBlock>
        <AccessFrame>
          <AccessTitle>Password Recovery</AccessTitle>
          <SubStatus>Enter your email to reset password</SubStatus>

          <form onSubmit={handleSubmit} autoComplete="off">
            {error && <ErrorMsg>{error}</ErrorMsg>}

            <InputBlock>
              <InputLabel>Email Address</InputLabel>
              <InputField
                type="email"
                value={email}
                onChange={e => setEmail(e.target.value)}
                placeholder="Enter your email"
                required
                autoFocus
              />
              <StatusText>Recovery instructions will be sent to this email</StatusText>
            </InputBlock>

            <SubmitButton type="submit" disabled={loading || !email}>
              {loading ? 'Processing...' : 'Send Reset Code'}
            </SubmitButton>
          </form>

          <LinkRow>
            <FrameLink to="/login">Back to Login</FrameLink>
            <FrameLink to="/register">Create Account</FrameLink>
          </LinkRow>
        </AccessFrame>
      </CenterBlock>
    </Background>
  );
};

export default ForgotPasswordPage;
